import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { useStandardsBallotContext } from '../StandardsBallotContext';
import routes from 'store/configs/Routes';
import CardComponent from 'components/common/CardComponent';
import CardFieldRowComponent from 'components/common/CardFieldRowComponent';
import Spinner from 'components/shared/Spinner';
import SignInLink from 'components/shared/SignInLink';
import type { CommitteeCycleModel } from 'store/types/StandardsBallot';
import { useAuthContext } from 'AuthContext';
import CommitteeModel from 'store/types/Committee';
import { conf } from 'config';

interface CommitteeCycleGroups {
  submittedCommitteeCycles: CommitteeCycleModel[];
  unSubmittedCommitteeCycles: CommitteeCycleModel[];
}

const MyStandardsBallotsComponent: React.FunctionComponent = () => {
  const { user, profile, profileLoading, profileError } = useAuthContext();
  const { committeeCycles, ballotDataLoading, cycleVotes, cycleVotesLoading } = useStandardsBallotContext();
  const navigate = useNavigate();

  const loading = useMemo(
    () => profileLoading || ballotDataLoading || cycleVotesLoading,
    [ballotDataLoading, cycleVotesLoading, profileLoading]
  );

  const committeeCycleGroups: CommitteeCycleGroups = useMemo(() => {
    const submittedCommitteeCycles: CommitteeCycleModel[] = [];
    const unSubmittedCommitteeCycles: CommitteeCycleModel[] = [];

    if (!cycleVotes || !committeeCycles) return { submittedCommitteeCycles: [], unSubmittedCommitteeCycles: [] };

    committeeCycles.forEach((cycle) => {
      if (cycleVotes.some((v) => v.committee === cycle.committee && v.period === cycle.period)) {
        submittedCommitteeCycles.push(cycle);
      } else {
        unSubmittedCommitteeCycles.push(cycle);
      }
    });

    return {
      submittedCommitteeCycles,
      unSubmittedCommitteeCycles,
    };
  }, [committeeCycles, cycleVotes]);

  const isStandardsBallotRequired = useCallback(
    (committeeCycle: CommitteeCycleModel) =>
      profile?.committee?.standards.some(
        (committee: CommitteeModel) =>
          committee.committeename === committeeCycle.committee && committee.status === conf.active
      ) ?? false,
    [profile?.committee.standards]
  );

  return (
    <div id="myStandardsBallotsComponent" className="container my-5">
      <Spinner loading={loading} transparent={false} className="h-100">
        {!user || !profile ? (
          <Alert severity="error" className="mb-3">
            Your session has expired or you are not logged in. Please <SignInLink>Sign In</SignInLink> and try again.
          </Alert>
        ) : profileError ? (
          <Alert severity="error" className="mb-3">
            Failed to load account. Please refresh the page or try again later.
          </Alert>
        ) : (
          <>
            <div className="row">
              <div className="col-12 col-lg-12">
                <CardComponent title="Instructions">
                  <div className="mb-3 description-text">
                    <p>
                      Please review documents located on the{' '}
                      <Link to={routes.standardsBallot.root} target="_blank" rel="noopener noreferrer">
                        ballot page
                      </Link>
                      .
                    </p>
                  </div>
                </CardComponent>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-lg-6">
                <CardComponent title="Ballots">
                  {committeeCycleGroups.unSubmittedCommitteeCycles.map((cycle: CommitteeCycleModel, i: number) =>
                    cycle.display ? (
                      <CardFieldRowComponent
                        key={i}
                        title={`${cycle.committee} ${cycle.period}`}
                        subtitle={isStandardsBallotRequired(cycle) ? 'REQUIRED' : 'OPTIONAL'}
                        onClick={() =>
                          navigate(
                            `${routes.standardsBallot.myBallots}/${encodeURIComponent(
                              cycle.committee
                            )}/${encodeURIComponent(cycle.period)}`
                          )
                        }
                        includeDivider
                      />
                    ) : (
                      <div key={i} />
                    )
                  )}
                </CardComponent>
              </div>
              <div className="col-12 col-lg-6">
                <CardComponent title="Submitted Ballots">
                  {committeeCycleGroups.submittedCommitteeCycles.length > 0 ? (
                    committeeCycleGroups.submittedCommitteeCycles.map((cycle: CommitteeCycleModel, i: number) => (
                      <CardFieldRowComponent
                        key={i}
                        title={`${cycle.committee} ${cycle.period}`}
                        subtitle="COMPLETED"
                        onClick={() =>
                          navigate(
                            `${routes.standardsBallot.myBallots}/${encodeURIComponent(
                              cycle.committee
                            )}/${encodeURIComponent(cycle.period)}`
                          )
                        }
                        includeDivider
                      />
                    ))
                  ) : (
                    <div className="text-center mt-5 mb-5">
                      <i className="description-text">No ballot response submitted.</i>
                    </div>
                  )}
                </CardComponent>
              </div>
            </div>
          </>
        )}
      </Spinner>
    </div>
  );
};

export default MyStandardsBallotsComponent;
